<script setup>
import { ref, computed, nextTick, watch, onMounted, onUnmounted, onDeactivated, onActivated } from 'vue';
import merge from 'lodash.merge';
import * as echarts from 'echarts/core';
import { LineChart, BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import {
	GridComponent,
	SingleAxisComponent,
	ToolboxComponent,
	TooltipComponent,
	AxisPointerComponent,
	BrushComponent,
	TitleComponent,
	LegendComponent,
	DataZoomComponent
} from 'echarts/components';
import { useCommonData } from '@/hooks';
import { convertNumberToStr, debounce } from '@/utils/api';
import { echartsTooltipFormatterSingle, echartsTooltipFormatterMultiple, flatData, convertPX } from '@/utils/echarts';
import { calcNiceTicks } from '@/utils/scale';

echarts.use([
	LineChart,
	BarChart,
	GridComponent,
	SingleAxisComponent,
	ToolboxComponent,
	TooltipComponent,
	AxisPointerComponent,
	BrushComponent,
	TitleComponent,
	LegendComponent,
	DataZoomComponent,
	CanvasRenderer
]);

const props = defineProps({
	// 是否显示tooltip的轴值，默认为false,如果为true，则tooltip的轴值会显示在tooltip的标题中,并且会覆盖props.title
	showToolTipAxisValue: {
		type: Boolean,
		default: false
	},
	title: {
		type: String,
		default: ''
	},
	xAxisData: {
		type: Array
	},
	seriesData: {
		type: Array,
		required: true,
		default: () => []
	},
	chartOptions: {
		type: Object,
		default: () => ({})
	},
	isReverse: {
		type: Boolean,
		default: false
	},
	customStyle: {
		type: String,
		default: ''
	},
	tooltipLabel: {
		type: String,
		default: ''
	},
	timeFormatter: {
		type: Function,
		default: value => value
	},
	// tooltipFormatterOrder tooltip是否排序，默认为空，不排序，如果为 desc 则降序，如果为 asc 则升序
	tooltipFormatterOrder: {
		type: String,
		default: ''
	},
	yLeftAxisLabelFormatter: {
		type: Function,
		default: value => {
			return convertNumberToStr(value);
		}
	},
	isSingleColor: {
		type: Boolean,
		default: false
	},
	isMobile: {
		type: Boolean,
		default: false
	},
	legends: {
		type: Array,
		default: () => []
	},
});
const emits = defineEmits(['data-checked']);

const showChart = computed(() => {
	return props.seriesData.some(series => {
		return series.data?.some(ele => {
			let value;
			if (Array.isArray(ele)) {
				value = ele[1];
			} else if (typeof ele === 'object') {
				value = ele.value;
			} else {
				value = ele;
			}
			return !['', undefined, null, NaN].includes(value);
		});
	});
});

watch(
	() => props.showChart,
	newVal => {
		isEmpty.value = !newVal;
	},
	{
		deep: true
	}
);

const isEmpty = ref(false);
let chart = null;
const chartRef = ref(null);
let lastWindowWidth = window.innerWidth;

// 监听窗口宽度变化，重新初始化图表
const handleResize = debounce(() => {
	const currentWidth = window.innerWidth;
	// 只有当宽度真正发生变化时才重新初始化
	if (currentWidth !== lastWindowWidth && showChart.value) {
		lastWindowWidth = currentWidth;
		nextTick(() => {
			initChart();
		});
	}
}, 300);

watch(showChart, newVal => {
	if (!newVal) {
		clearChart();
	}
});
watch(
	() => props.seriesData,
	() => {
		if (showChart.value) {
			nextTick(() => {
				initChart();
			});
		}
	},
	{
		immediate: true,
		deep: true
	}
);
const openCustomStyle = computed(() => ['fund_holding'].includes(props.customStyle));

// 解析 CSS 变量或返回颜色值
function getColorValue(color) {
	const { colorVar, opacity } = color;
	if (!colorVar) return null;
	// 如果是 CSS 变量格式 (var(--variable-name))
	if (typeof colorVar === 'string' && colorVar.startsWith('var(')) {
		// 提取变量名
		const varName = colorVar.match(/var\(([^)]+)\)/)?.[1];
		if (varName) {
			// 优先从 chartRef 元素获取，如果没有则从根元素获取
			const element = chartRef.value || document.documentElement;
			const value = getComputedStyle(element).getPropertyValue(varName.trim()).trim();

			return opacity ? `rgba(${value || colorVar},${opacity})` : value || colorVar; // 如果获取不到，返回原值
		}
	}
}

// 处理颜色数组
function processColors(colors) {
	if (colors instanceof Array) {
		return colors.map(color => getColorValue(color)).filter(Boolean);
	} else {
		return getColorValue(colors);
	}
}

const colors = computed(() =>
	processColors(
		props.isSingleColor
			? [{ colorVar: 'var(--surf-inverse-color-rgb)', opacity: 0.75 }, { colorVar: 'var(--surf-inverse-color)' }]
			: [
					{ colorVar: 'var(--surf-inverse-color-rgb)', opacity: 0.1 },
					{ colorVar: 'var(--surf-inverse-color-rgb)', opacity: 0.2 },
					{ colorVar: 'var(--surf-inverse-color-rgb)', opacity: 0.5 },
					{ colorVar: 'var(--surf-inverse-color-rgb)', opacity: 0.8 }
			  ]
	)
);

// 考虑负数情况，将数据转换成带有起始和结束位置的堆叠数据
function computeStackedData(data) {
	const cols = data[0].length;
	const rows = data.length;
	const stackedData = Array(rows)
		.fill(0)
		.map(() => new Array(cols).fill(0));
	const columnSums = new Array(cols).fill(0);

	for (let row = 0; row < rows; row++) {
		for (let col = 0; col < cols; col++) {
			const value = data[row][col];
			// 起始位置为当前列的累加和
			const start = columnSums[col];
			// 结束位置为起始位置加上当前值（可正可负）
			const end = start + value;
			// 将起始、结束和原始值存入结果数组
			stackedData[row][col] = { start, end, value };
			// 更新该列的累加和，为下一个数据点的起始位置做准备
			columnSums[col] = end;
		}
	}

	return stackedData;
}

// 确定 Y 轴范围
const getMinMax = stackedData => {
	const flatData = stackedData.flat();
	const starts = flatData.map(d => d.start);
	const ends = flatData.map(d => d.end);
	const values = flatData.map(d => d.value);

	// 合并所有可能影响Y轴范围的值（起始、结束和原始值）
	const allValues = [...starts, ...ends, ...values];
	const min = Math.min(...allValues);
	const max = Math.max(...allValues);

	return { min, max };
};

function initChart() {
	chart?.clear();
	chart?.dispose();
	if (!chartRef.value) return;
	chart = echarts.init(chartRef.value, {
		devicePixelRatio: 2
	});

	let xAxisData = props.xAxisData;
	if (!xAxisData) {
		xAxisData = props.seriesData[0].data.map(ele => {
			if (Array.isArray(ele)) {
				return ele[0];
			} else {
				return ele.name;
			}
		});
	}

	const yAxisData = props.seriesData
		.filter(series => series.type != 'line')
		.map(series =>
			series.data.map(ele => {
				if (Array.isArray(ele)) {
					return ele[1];
				} else {
					return ele.value;
				}
			})
		)
		.filter(val => !['', '-', undefined, null, NaN].includes(val));

	const stackedData = computeStackedData(yAxisData);
	const { min, max } = getMinMax(stackedData);
	const ticks = calcNiceTicks([min, max]);

	const options = {
		grid: {
			top: convertPX(16),
			left: props.isMobile ? 0 : convertPX(12),
			right: props.isMobile ? 0 : convertPX(12),
			bottom: props.isMobile ? 0 : convertPX(30),
			containLabel: true
		},
		color: colors.value,
		animation: false,
		legend: {
			bottom: 0,
			padding: 0,
			itemGap: 16,
			itemWidth: convertPX(10),
			itemHeight: convertPX(10),
			inactiveColor: 'C9CDD4',
			textStyle: {
				color: processColors({ colorVar: 'var(--surf-inverse-color)' }),
				fontSize: convertPX(13),
				lineHeight: convertPX(18),
				padding: [0, 0, 0, convertPX(6)],
				height: convertPX(18)
			},
			icon: 'roundRect',
			selectedMode: false
		},
		tooltip: {
			trigger: 'axis',
			// appendToBody: true,
			confine: true,
			extraCssText: `padding: ${convertPX(8)}px ${convertPX(12)}px ${convertPX(
				12
			)}px;border-radius: 2px;background-color: ${processColors({
				colorVar: 'var(--surf-secondary-color)'
			})};box-shadow: 0 10px 20px 0 ${processColors({
				colorVar: 'var(--surf-theme-color-rgb)',
				opacity: 0.4
			})};border: 1px solid ${processColors({
				colorVar: 'var(--surf-theme-color-rgb)',
				opacity: 0.3
			})};z-index: 6;`,
			axisPointer: {
				type: 'line',
				snap: true,
				lineStyle: {
					color: '#E8EAEF',
					width: 1,
					type: 'dashed'
				},
				handle: {
					show: false
				}
			},
			formatter: function (params) {
				if (props.isReverse) {
					params.reverse();
				}
				let tooltipTitle = props.title;
				if (props.showToolTipAxisValue && params?.[0]?.axisValue) {
					tooltipTitle = params[0].axisValue;
				}
				if (params.length === 1) {
					const item = params[0];
					let value, unit;
					// item.data.value TODO: 不应该取item.data.value，而是取item.data
					let deepValue = item.data; // 取值结构兼容

					// 如果数据被 onChartChecked 包装过（有 itemStyle 属性），需要从 value 中提取实际值
					if (typeof deepValue === 'object' && deepValue.itemStyle && deepValue.value !== undefined) {
						deepValue = deepValue.value;
					}

					if (Array.isArray(deepValue)) {
						value = deepValue[1];
						unit = deepValue[2];
					} else if (typeof deepValue === 'object') {
						value = deepValue.value;
						unit = deepValue.unit;
					} else {
						value = deepValue;
					}
					return echartsTooltipFormatterSingle(
						tooltipTitle,
						item.color,
						[
							{
								name: props.tooltipLabel || item.name,
								value:
									unit === '%' ? value?.toFixed(2) + unit : convertNumberToStr(value) + (unit || '')
							}
						],
						'echarts-custom-tooltip'
					);
				} else {
					const formatParams = params
						.map(item => {
							let value, unit;
							let deepValue = item.data;

							// 如果数据被 onChartChecked 包装过（有 itemStyle 属性），需要从 value 中提取实际值
							if (typeof deepValue === 'object' && deepValue.itemStyle && deepValue.value !== undefined) {
								deepValue = deepValue.value;
							}

							if (Array.isArray(deepValue)) {
								value = deepValue[1];
								unit = deepValue[2];
							} else if (typeof deepValue === 'object') {
								value = deepValue.value;
								unit = deepValue.unit;
							} else {
								value = deepValue;
							}
							return {
								color: item.color,
								seriesName: item.seriesName,
								originValue: value,
								value:
									value != null
										? unit === '%'
											? value.toFixed(2) + unit
											: convertNumberToStr(value) + (unit || '')
										: null
							};
						})
						.filter(ele => ele.value != null);
					// 排序
					if (props.tooltipFormatterOrder == 'desc') {
						formatParams.sort((a, b) => b.originValue - a.originValue);
					} else if (props.tooltipFormatterOrder == 'asc') {
						formatParams.sort((a, b) => a.originValue - b.originValue);
					}
					return echartsTooltipFormatterMultiple(tooltipTitle, formatParams, 'echarts-custom-tooltip');
				}
			}
		},
		xAxis: {
			type: 'category',
			axisLine: {
				show: false
			},
			axisTick: {
				show: false
			},
			axisLabel: {
				show: true,
				margin: convertPX(8),
				color: processColors({ colorVar: 'var(--surf-inverse-color)' }),
				fontSize: convertPX(12),
				align: 'center',
				lineHeight: convertPX(16),
				formatter: function (value) {
					return props.timeFormatter?.(value) || value;
				}
			},
			splitLine: {
				show: false
			},
			data: xAxisData
		},
		yAxis: [
			{
				type: 'value',
				axisLine: {
					show: false
				},
				axisTick: {
					show: false
				},
				axisLabel: {
					show: false,
					color: processColors({ colorVar: 'var(--surf-inverse-color)' }),
					fontSize: convertPX(12),
					lineHeight: convertPX(16),
					formatter: props.yLeftAxisLabelFormatter
				},
				splitLine: {
					show: false
				},
				// minInterval: 1,
				splitNumber: 5,
				...ticks
			},
			{
				type: 'value',
				show: false,
				axisLabel: {
					show: false
				},
				axisLine: {
					show: false
				},
				axisTick: {
					show: false
				},
				splitLine: {
					show: false
				},
				splitNumber: 5
			}
		],
		series: props.seriesData.map(series => {
			if (series.type == 'bar') {
				console.log('🚀 ~ series:', series);
				return merge(
					{
						silent: true,
						barMaxWidth: convertPX(32),
						barCategoryGap: 4,
						large: true,
						itemStyle: {
							borderRadius: [convertPX(4), convertPX(4), 0, 0] // 顶部圆角，底部不圆角
						},
						emphasis: {
							itemStyle: {
								color: processColors({ colorVar: 'var(--surf-inverse-color-rgb)', opacity: 0.9 }),
								borderRadius: [convertPX(4), convertPX(4), 0, 0]
							},
							disabled: openCustomStyle.value && props.isMobile
						}
					},
					preformatSeries(series)
				);
			} else if (series.type == 'line') {
				return merge(
					{
						type: 'line',
						showSymbol: false,
						lineStyle: {
							width: series.width || 1
						},
						itemStyle: {
							color: processColors({ colorVar: 'var(--surf-inverse-color)' })
						}
					},
					series
				);
			}
		})
	};
	chart.setOption(merge(options, props.chartOptions));

	const _series = options.series;
	lastActiveUpdate(_series);

	
	// zrender 事件与 echarts 事件不同。前者是当鼠标在任何地方都会被触发，而后者是只有当鼠标在图形元素上时才能被触发。
	chart.getZr().on('mouseup', event => {
		const pos = [event.offsetX, event.offsetY];
		// 检查点击位置是否在图表的网格区域内
		if (chart.containPixel('grid', pos)) {
			// 将像素坐标转换为数据索引
			let [dataIndex] = chart.convertFromPixel({ seriesIndex: 0 }, pos);
			const newSeries = onChartChecked(dataIndex, _series);
			// 重新更新图表数据
			newSeries &&
				chart.setOption({
					series: newSeries
				});
		}
	});
}

const activeIndex = ref(-1);
const lastActiveUpdate = series => {
	if (!openCustomStyle.value) return;
	if (series?.[0].data.length) {
		activeIndex.value = series[0].data.length - 1;
	}
};

const preformatSeries = seriesItem => {
	if (!openCustomStyle.value) return seriesItem;
	const { data, ...rest } = seriesItem;
	return {
		...rest,
		data: data.map((val, index) => {
			return {
				value: val,
				itemStyle: {
					// 默认选中最后（最新）一根柱子
					color:
						index === data.length - 1
							? processColors({ colorVar: 'var(--surf-inverse-color-rgb)', opacity: 0.8 })
							: processColors({ colorVar: 'var(--surf-inverse-color-rgb)', opacity: 0.1 })
				}
			};
		})
	};
};

const onChartChecked = (idx, _series) => {
	if (!openCustomStyle.value) return;
	if (typeof idx !== 'number') return;

	const dataIndex = Math.abs(idx);
	const sameClickedIndex = activeIndex.value === dataIndex;
	const clickedIndex = dataIndex;
	activeIndex.value = clickedIndex;

	let pickValue;
	const newSeries = _series.map(series => {
		const { data, ...rest } = series;
		return {
			...rest,
			data: data.map((item, index) => {
				const deepValue = item.value || item;
				if (index === clickedIndex) pickValue = deepValue;
				return {
					value: deepValue,
					itemStyle: {
						color:
							index === clickedIndex
								? processColors({ colorVar: 'var(--surf-inverse-color-rgb)', opacity: 0.8 })
								: processColors({ colorVar: 'var(--surf-inverse-color-rgb)', opacity: 0.1 })
					}
				};
			})
		};
	});

	if (sameClickedIndex) {
		// emits('data-checked', { index: null, data: null });
		// console.log('-------- 反选');
		// activeIndex.value = -1; // reset
	} else {
		emits('data-checked', { index: clickedIndex, data: pickValue[0] });
		// console.log('-------- 正选', clickedIndex, pickValue);
	}

	return newSeries;
};

function clearChart() {
	chart?.dispatchAction({
		type: 'hideTip'
	});
	chart?.clear();
	chart?.dispose();
	chart = null;
}

onMounted(() => {
	// 初始化窗口宽度
	lastWindowWidth = window.innerWidth;
	// 监听窗口大小变化
	window.addEventListener('resize', handleResize);
});

onActivated(() => {
	if (showChart.value) {
		nextTick(() => {
			initChart();
		});
	}
});
onUnmounted(() => {
	// 移除窗口大小变化监听
	window.removeEventListener('resize', handleResize);
	clearChart();
});
onDeactivated(() => {
	clearChart();
});

</script>

<template>
	<div class="line-bar-chart" v-loading="!seriesData.length">
		<div v-if="showChart" class="chart-main">
			<div class="chart-canvas" ref="chartRef"></div>
			<div v-if="legends.length" class="legend-container">
				<div class="legend-wrapper" v-for="(item, index) in legends" :key="index">
					<span class="legend-item" :style="{ background: colors[index] }"></span
					><span class="legend-word">{{ item }}</span>
				</div>
			</div>
		</div>
		<Empty v-if="isEmpty" class="chart-empty"></Empty>
	</div>
</template>

<style lang="less" scoped>
.line-bar-chart,
.chart-empty {
	width: 100%;
	height: 100%;
	.chart-main {
		position: relative;
		height: 100%;
		display: flex;
		flex-direction: column;

		.chart-canvas {
			flex: 1;
			min-height: 0;
		}
	}
}
.chart-mask {
	position: absolute;
	left:0;
	right:0;
	height:20px;
	user-select: none;
	-webkit-user-select: none;
	-moz-user-select: none;
	-ms-user-select: none;
}
</style>
<style lang="less">
.echarts-custom-tooltip {
	.echarts-tooltip-name {
		display: flex;
		align-items: center;

		font-family: 'Barlow';

		font-weight: 400;
		font-size: 12px;
		line-height: 20px;
		color: #1d2129;
	}

	.echarts-tooltip-grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		grid-column-gap: 4px;
	}

	.echarts-tooltip-wrapper {
		margin-top: 4px;
		min-width: 100px;
		padding: 4px 6px;
		background-color: rgba(var(--surf-inverse-color-rgb), 0.1);

		display: flex;
		align-items: center;
		justify-content: space-between;

		font-weight: 400;
		font-size: 12px;
	}

	.echarts-tooltip-item-name {
		display: flex;
		align-items: center;

		color: rgba(var(--surf-inverse-color-rgb), 0.6);
	}

	.echarts-tooltip-item-value {
		margin-left: 16px;

		font-family: 'Barlow';
		color: rgba(var(--surf-inverse-color-rgb), 0.6);
	}
}

.legend-container {
	margin: 0 auto;
	padding-top: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-wrap: wrap;
	gap: 4px 16px;

	.legend-wrapper {
		display: flex;
		align-items: center;
		gap: 3px;
	}
	.legend-item {
		// margin: 3px;
		width: 10px;
		height: 10px;
		border-radius: 2px;
	}
	.legend-word {
		color: rgba(var(--surf-inverse-color-rgb), 0.6);
		text-align: right;
		font-size: 13px;
		font-weight: 400;
		line-height: 1; /* 123.077% */
		white-space: nowrap;
	}
}
</style>
